// /var/www/afra/192.testproject.work/maracar8/0.8.2/myapp/src/components/LexicalEditor/DragDrop.js
import React, { useState, useEffect } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getSelection, $isRangeSelection, $getRoot, $createParagraphNode
} from 'lexical';
import { $createImageNode } from '../Content/LexicalEditor'; // import จาก LexicalEditor
import { API_URLS } from '../../api/api';

const DragDrop = ({ fieldName, onImageUpload, isUploading, setIsUploading }) => {
  const [editor] = useLexicalComposerContext();
  const [draggedNodeKey, setDraggedNodeKey] = useState(null);
  const [dropIndicatorPos, setDropIndicatorPos] = useState(null);

  const insertImageToEditor = (imageUrl) => {
    editor.update(() => {
      const selection = $getSelection();
      const imageNode = $createImageNode({ src: imageUrl, altText: 'Uploaded image' });
      if ($isRangeSelection(selection)) {
        selection.insertNodes([imageNode]);
      } else {
        const root = $getRoot();
        const paragraph = $createParagraphNode();
        paragraph.append(imageNode);
        root.append(paragraph);
      }
    });
  };

  const uploadImageToServer = async (file) => {
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    setIsUploading(true);
    try {
      const response = await fetch(API_URLS.IMAGE_UPLOAD, {
        method: 'POST',
        body: formDataUpload,
      });
      if (!response.ok) throw new Error(`Failed to upload image: ${response.statusText}`);
      const result = await response.json();
      if (!result.resizedFileURLs || !result.originalFileURLs) throw new Error('Invalid response format');
      return { resizedUrl: result.resizedFileURLs[0], originalUrl: result.originalFileURLs[0] };
    } catch (error) {
      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  const handleExternalDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer?.files?.length) {
      const file = e.dataTransfer.files[0];
      if (file.type.startsWith('image/')) {
        try {
          const { resizedUrl } = await uploadImageToServer(file);
          onImageUpload(fieldName, file, resizedUrl);
          insertImageToEditor(resizedUrl);
        } catch (err) {
          console.error('Upload error (drag-drop):', err);
          alert(`Failed to upload image: ${err.message}`);
          onImageUpload(fieldName, null, '');
        }
      }
    }
  };

  const getDropPosition = (e) => {
    const range = document.caretRangeFromPoint(e.clientX, e.clientY);
    if (!range || !editor.getRootElement().contains(range.startContainer)) return null;
    const rect = range.getBoundingClientRect();
    return { range, y: rect.top - editor.getRootElement().getBoundingClientRect().top };
  };

  const handleDragStart = (e) => {
    const key = e.target.getAttribute('data-key');
    if (key) {
      setDraggedNodeKey(key);
      e.dataTransfer.setData('text/plain', key);
      e.target.classList.add('dragging');
      const dragPreview = e.target.cloneNode(true);
      dragPreview.style.opacity = '0.5';
      dragPreview.style.position = 'absolute';
      dragPreview.style.pointerEvents = 'none';
      document.body.appendChild(dragPreview);
      e.dataTransfer.setDragImage(dragPreview, e.offsetX, e.offsetY);
      setTimeout(() => document.body.removeChild(dragPreview), 0);
    } else {
      e.preventDefault();
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    const dropPos = getDropPosition(e);
    if (dropPos) {
      setDropIndicatorPos(dropPos.y);
      editor.getRootElement().classList.add('drag-over');
    }
  };

  const handleDragLeave = () => {
    editor.getRootElement().classList.remove('drag-over');
    setDropIndicatorPos(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const editorElement = editor.getRootElement();
    editorElement.classList.remove('drag-over');
    const droppedKey = e.dataTransfer.getData('text/plain');
    if (!droppedKey) {
      handleExternalDrop(e);
      return;
    }
    if (droppedKey !== draggedNodeKey) return;

    editor.update(() => {
      const nodeToMove = editor.getEditorState()._nodeMap.get(draggedNodeKey);
      if (!nodeToMove || nodeToMove.getType() !== 'image') {
        console.error('Invalid node to move:', nodeToMove);
        return;
      }

      const parent = nodeToMove.getParent();
      if (!parent) {
        console.error('Node has no parent:', nodeToMove);
        return;
      }

      nodeToMove.remove();

      const dropPos = getDropPosition(e);
      if (!dropPos) {
        const root = $getRoot();
        const paragraph = $createParagraphNode();
        paragraph.append(nodeToMove);
        root.append(paragraph);
        return;
      }

      const selection = $getSelection();
      const newNode = $createImageNode({ src: nodeToMove.__src, altText: nodeToMove.__altText });

      if ($isRangeSelection(selection)) {
        selection.insertNodes([newNode]);
      } else {
        const root = $getRoot();
        const paragraph = $createParagraphNode();
        paragraph.append(newNode);
        root.append(paragraph);
      }
    });
    setDraggedNodeKey(null);
    setDropIndicatorPos(null);
  };

  useEffect(() => {
    const editorElement = editor.getRootElement();
    if (!editorElement) return;
    editorElement.addEventListener('dragstart', handleDragStart);
    editorElement.addEventListener('dragover', handleDragOver);
    editorElement.addEventListener('dragleave', handleDragLeave);
    editorElement.addEventListener('drop', handleDrop);
    return () => {
      editorElement.removeEventListener('dragstart', handleDragStart);
      editorElement.removeEventListener('dragover', handleDragOver);
      editorElement.removeEventListener('dragleave', handleDragLeave);
      editorElement.removeEventListener('drop', handleDrop);
    };
  }, [editor, draggedNodeKey]);

  return dropIndicatorPos !== null ? (
    <div
      className="drop-indicator"
      style={{
        position: 'absolute',
        top: `${dropIndicatorPos}px`,
        left: 0,
        right: 0,
        height: '2px',
        background: '#007bff',
        transition: 'top 0.1s ease',
        zIndex: 10,
      }}
    />
  ) : null;
};

export default DragDrop;